import { useRef, useState } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useUsageGate } from "@/hooks/useUsageGate";
import { endToolSession } from "@/lib/usageGate";
import { wouldMarkSvg, markSvgText } from "@/lib/trialMark";
import { stripUiAttributes, buildAvatarSvg, downloadSignature } from "./avatarExport";

const SKIN_COLORS = [
  "#FFDBB4",
  "#EDB98A",
  "#D08B5B",
  "#AE5D29",
  "#7A4419",
  "#F1C27D",
];
const HAIR_COLORS = [
  "#2C1B18",
  "#4A2E22",
  "#A55728",
  "#D2A24C",
  "#E8E1E1",
  "#1C1C1C",
  "#8B3A3A",
];
const BG_COLORS = [
  "#818CF8",
  "#F472B6",
  "#34D399",
  "#FBBF24",
  "#60A5FA",
  "#A78BFA",
  "#F87171",
];

type HairStyle = "short" | "bald" | "bun" | "long" | "spiky";
type EyeStyle = "normal" | "happy" | "wink" | "surprised" | "sad" | "angry" | "closed" | "hearts";
type MouthStyle = "smile" | "grin" | "neutral" | "open" | "sad" | "smirk" | "tongue" | "worried";

function HairSvg({ style, color, faceShape }: { style: HairStyle; color: string; faceShape: FaceShape }) {
  if (style === "bald") return null;
  // All paths below are hand-tuned for the round face's anchor width
  // (55-145, see the comment on HAIR_ANCHOR_BY_SHAPE above). Rather than
  // duplicate every path three times for oval/square, this scales the
  // whole hair shape horizontally around its own center (x=100) to match
  // whichever face shape is actually selected, using the real ratio
  // between that shape's anchor width and the round face's.
  const anchor = HAIR_ANCHOR_BY_SHAPE[faceShape];
  const scaleX = (anchor.right - anchor.left) / (145 - 55);
  const content = (() => {
    if (style === "short")
      return (
        <path
          d="M55,58 Q55,18 100,18 Q145,18 145,58 Q100,38 55,58 Z"
          fill={color}
        />
      );
    if (style === "bun")
      return (
        <>
          <path
            d="M55,60 Q55,20 100,20 Q145,20 145,60 Q100,40 55,60 Z"
            fill={color}
          />
          <circle cx="100" cy="16" r="15" fill={color} />
        </>
      );
    if (style === "long")
      return (
        <path
          d="M52,63 Q48,20 100,17 Q152,20 148,63 L148,150 Q136,158 133,128 L128,63 Q100,48 72,63 L67,128 Q64,158 52,150 Z"
          fill={color}
        />
      );
    // spiky
    return (
      <path
        d="M55,58 L62,15 L75,42 L88,8 L100,40 L112,8 L125,42 L138,15 L145,58 Q100,32 55,58 Z"
        fill={color}
      />
    );
  })();

  return (
    <g transform={`translate(100,0) scale(${scaleX},1) translate(-100,0)`}>
      {content}
    </g>
  );
}

type FaceShape = "round" | "oval" | "square";

// Eyes, mouth, and glasses are all drawn at fixed coordinates
// independent of the face outline (not computed from its geometry), so
// changing the face shape doesn't require moving them -- only the
// outline itself, and the hair anchor points, need to vary per shape.
function FaceSvg({ shape, color }: { shape: FaceShape; color: string }) {
  if (shape === "oval")
    return <ellipse cx="100" cy="108" rx="56" ry="78" fill={color} />;
  if (shape === "square")
    return <rect x="42" y="48" width="116" height="115" rx="22" fill={color} />;
  return <circle cx="100" cy="105" r="65" fill={color} />;
}

// Each shape's own boundary at y=58 (the height HairSvg's anchor points
// are built for) -- computed directly from that shape's actual geometry,
// the same rigor used for the round face's original fix, so hair sits
// correctly on all three shapes rather than just the default one.
// Round: circle (cx100,cy105,r65) -> x = 100 +/- sqrt(65^2-47^2) ~= 55/145
// Oval: ellipse (cx100,cy108,rx56,ry78) -> at y=58, ((58-108)/78)^2=0.411,
//   x = 100 +/- 56*sqrt(1-0.411) ~= 100 +/- 42.9 ~= 57/143
// Square: rect (x42,w116,rx22) top-left corner curves in slightly at y=58
//   (10px below the rect's y=48 top edge, inside the 22px corner radius)
//   -> roughly x = 47/153, a touch wider than the round/oval anchors
const HAIR_ANCHOR_BY_SHAPE: Record<FaceShape, { left: number; right: number }> = {
  round: { left: 55, right: 145 },
  oval: { left: 57, right: 143 },
  square: { left: 47, right: 153 },
};

function EyesSvg({ style }: { style: EyeStyle }) {
  if (style === "happy")
    return (
      <>
        <path
          d="M65,95 Q75,85 85,95"
          stroke="#2c2c2c"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M115,95 Q125,85 135,95"
          stroke="#2c2c2c"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
      </>
    );
  if (style === "wink")
    return (
      <>
        <circle cx="75" cy="92" r="6" fill="#2c2c2c" />
        <path
          d="M115,92 Q125,88 135,92"
          stroke="#2c2c2c"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
      </>
    );
  if (style === "surprised")
    return (
      <>
        <circle cx="75" cy="92" r="8" fill="#2c2c2c" />
        <circle cx="125" cy="92" r="8" fill="#2c2c2c" />
      </>
    );
  if (style === "sad")
    return (
      <>
        <path
          d="M67,88 Q75,98 83,90"
          stroke="#2c2c2c"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M117,88 Q125,98 133,90"
          stroke="#2c2c2c"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
      </>
    );
  if (style === "angry")
    return (
      <>
        <path
          d="M66,84 L86,92"
          stroke="#2c2c2c"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <circle cx="80" cy="94" r="5" fill="#2c2c2c" />
        <path
          d="M134,84 L114,92"
          stroke="#2c2c2c"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <circle cx="120" cy="94" r="5" fill="#2c2c2c" />
      </>
    );
  if (style === "closed")
    return (
      <>
        <path
          d="M67,92 L83,92"
          stroke="#2c2c2c"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path
          d="M117,92 L133,92"
          stroke="#2c2c2c"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </>
    );
  if (style === "hearts")
    return (
      <>
        <path
          d="M75,86 c-4,-6 -14,-2 -12,6 c1.5,6 8,9 12,12 c4,-3 10.5,-6 12,-12 c2,-8 -8,-12 -12,-6z"
          fill="#e0555f"
        />
        <path
          d="M125,86 c-4,-6 -14,-2 -12,6 c1.5,6 8,9 12,12 c4,-3 10.5,-6 12,-12 c2,-8 -8,-12 -12,-6z"
          fill="#e0555f"
        />
      </>
    );
  return (
    <>
      <circle cx="75" cy="92" r="6" fill="#2c2c2c" />
      <circle cx="125" cy="92" r="6" fill="#2c2c2c" />
    </>
  );
}

function MouthSvg({ style }: { style: MouthStyle }) {
  if (style === "grin")
    return <path d="M75,120 Q100,140 125,120 Q100,132 75,120" fill="#7a3b3b" />;
  if (style === "neutral")
    return (
      <line
        x1="80"
        y1="122"
        x2="120"
        y2="122"
        stroke="#7a3b3b"
        strokeWidth="4"
        strokeLinecap="round"
      />
    );
  if (style === "open")
    return <ellipse cx="100" cy="123" rx="14" ry="10" fill="#7a3b3b" />;
  if (style === "sad")
    return (
      <path
        d="M78,128 Q100,112 122,128"
        stroke="#7a3b3b"
        strokeWidth="5"
        fill="none"
        strokeLinecap="round"
      />
    );
  if (style === "smirk")
    return (
      <path
        d="M80,120 Q100,126 122,112"
        stroke="#7a3b3b"
        strokeWidth="5"
        fill="none"
        strokeLinecap="round"
      />
    );
  if (style === "tongue")
    return (
      <>
        <path d="M78,116 Q100,132 122,116 Q100,124 78,116" fill="#7a3b3b" />
        <path d="M92,124 Q100,138 108,124 Z" fill="#e0777f" />
      </>
    );
  if (style === "worried")
    return (
      <path
        d="M78,124 Q86,118 94,124 Q100,120 106,124 Q114,118 122,124"
        stroke="#7a3b3b"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
    );
  return (
    <path
      d="M75,115 Q100,135 125,115"
      stroke="#7a3b3b"
      strokeWidth="5"
      fill="none"
      strokeLinecap="round"
    />
  );
}

const TOOL_INFO_CONTENT = {
  what: `Builds a custom cartoon-style avatar from a selection of hair styles, eyes, colors, and glasses — entirely parametric and hand-drawn, built from simple shapes, with no photo involved.`,
  how: `Choose options for skin tone, hair style and color, eyes, mouth, and background from the available choices, or use the randomize button, then download your avatar as a PNG (256, 512 or 1024 px, optionally circle-cropped with a transparent background) or an SVG.`,
  tip: `The randomize button is a genuinely fast way to find a starting combination you like, which you can then fine-tune individual features from rather than building one from scratch.`,
};

export default function AvatarBuilder() {
  const [skinColor, setSkinColor] = useState(SKIN_COLORS[0]);
  const [faceShape, setFaceShape] = useState<FaceShape>("round");
  const [hairColor, setHairColor] = useState(HAIR_COLORS[0]);
  const [bgColor, setBgColor] = useState(BG_COLORS[0]);
  const [hairStyle, setHairStyle] = useState<HairStyle>("short");
  const [eyeStyle, setEyeStyle] = useState<EyeStyle>("normal");
  const [mouthStyle, setMouthStyle] = useState<MouthStyle>("smile");
  const [glasses, setGlasses] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const randomize = () => {
    setSkinColor(SKIN_COLORS[Math.floor(Math.random() * SKIN_COLORS.length)]);
    setFaceShape((["round", "oval", "square"] as FaceShape[])[Math.floor(Math.random() * 3)]);
    setHairColor(HAIR_COLORS[Math.floor(Math.random() * HAIR_COLORS.length)]);
    setBgColor(BG_COLORS[Math.floor(Math.random() * BG_COLORS.length)]);
    setHairStyle(
      (["short", "bald", "bun", "long", "spiky"] as HairStyle[])[
        Math.floor(Math.random() * 5)
      ]
    );
    setEyeStyle(
      (["normal", "happy", "wink", "surprised", "sad", "angry", "closed", "hearts"] as EyeStyle[])[
        Math.floor(Math.random() * 8)
      ]
    );
    setMouthStyle(
      (["smile", "grin", "neutral", "open", "sad", "smirk", "tongue", "worried"] as MouthStyle[])[
        Math.floor(Math.random() * 8)
      ]
    );
    setGlasses(Math.random() > 0.5);
  };

  const { logUsageNow } = useLogUsageOnAction();
  const { toolId } = useUsageGate();

  const [exportSize, setExportSize] = useState(512);
  const [circle, setCircle] = useState(false);
  const [transparent, setTransparent] = useState(false);
  // Outputs already paid for in this visit: taking the very same file again costs nothing more.
  const paidFor = useRef(new Set<string>());

  // The picture as SVG text, with the background left out when "transparent" is on.
  const svgText = () => {
    const clone = svgRef.current!.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("width", String(exportSize));
    clone.setAttribute("height", String(exportSize));
    clone.removeAttribute("class");
    if (transparent) clone.querySelector("rect")?.remove();
    return stripUiAttributes(new XMLSerializer().serializeToString(clone));
  };

  // One trial use per distinct download (format, size, options and picture). The standard gate counts one
  // use per visit, so a different output starts a fresh count; at 0 uses left that is refused with the pass
  // dialog and nothing is saved. Re-taking the identical file is free.
  const allowDownload = async (format: "png" | "svg"): Promise<boolean> => {
    const sig = downloadSignature({
      format, exportSize, circle, transparent, skinColor, faceShape, hairColor, bgColor, hairStyle, eyeStyle, mouthStyle, glasses,
    });
    if (paidFor.current.has(sig)) return true;
    endToolSession(toolId);
    const allowed = await logUsageNow("download");
    if (allowed) paidFor.current.add(sig);
    return allowed;
  };

  const downloadPng = async () => {
    if (!svgRef.current) return;
    const url = URL.createObjectURL(new Blob([svgText()], { type: "image/svg+xml" }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = exportSize;
      canvas.height = exportSize;
      const ctx = canvas.getContext("2d")!;
      if (circle) { ctx.beginPath(); ctx.arc(exportSize / 2, exportSize / 2, exportSize / 2, 0, Math.PI * 2); ctx.clip(); }
      ctx.drawImage(img, 0, 0, exportSize, exportSize);
      URL.revokeObjectURL(url);
      canvas.toBlob(async blob => {
        if (!blob) { toast.error("Couldn't create the image"); return; }
        if (!(await allowDownload("png"))) return;
        // downloadBlob adds the single trial badge (lib/trialMark.ts) for trial sessions.
        downloadBlob(blob, `avatar-${exportSize}.png`);
        toast.success("Avatar downloaded");
      }, "image/png");
    };
    img.onerror = () => { URL.revokeObjectURL(url); toast.error("Couldn't create the image"); };
    img.src = url;
  };

  const downloadSvg = async () => {
    if (!svgRef.current) return;
    if (!(await allowDownload("svg"))) return;
    let text = buildAvatarSvg(svgText(), { circle });
    // The same single badge as the PNG, drawn outside the circle clip so a circle crop cannot hide it.
    if (wouldMarkSvg(toolId)) text = markSvgText(text, 200, 200);
    downloadBlob(new Blob([text], { type: "image/svg+xml" }), "avatar.svg");
    toast.success("Avatar downloaded");
  };

  const ColorRow = ({
    colors,
    value,
    onChange,
    name,
  }: {
    colors: string[];
    value: string;
    onChange: (c: string) => void;
    name: string;
  }) => (
    <div className="flex flex-wrap gap-2" role="group" aria-label={name}>
      {colors.map((c, i) => (
        <button
          key={c}
          type="button"
          aria-label={`${name} ${i + 1}`}
          aria-pressed={value === c}
          title={`${name} ${i + 1}`}
          onClick={() => onChange(c)}
          className={`w-8 h-8 rounded-full border-2 ${value === c ? "border-primary" : "border-transparent"}`}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );

  const StyleRow = ({
    options,
    value,
    onChange,
  }: {
    options: string[];
    value: string;
    onChange: (v: string) => void;
  }) => (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <Button
          key={o}
          size="sm"
          variant={value === o ? "default" : "outline"}
          aria-pressed={value === o}
          onClick={() => onChange(o)}
          className="capitalize"
        >
          {o}
        </Button>
      ))}
    </div>
  );

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="flex flex-col items-center gap-4">
          <svg
            ref={svgRef}
            viewBox="0 0 200 200"
            className="w-64 h-64 rounded-2xl border"
          >
            <rect width="200" height="200" fill={bgColor} />
            {/* face */}
            <FaceSvg shape={faceShape} color={skinColor} />
            <MouthSvg style={mouthStyle} />
            <EyesSvg style={eyeStyle} />
            {glasses && (
              <g stroke="#2c2c2c" strokeWidth="3" fill="none">
                <circle cx="75" cy="92" r="14" />
                <circle cx="125" cy="92" r="14" />
                <line x1="89" y1="92" x2="111" y2="92" />
              </g>
            )}
            <HairSvg style={hairStyle} color={hairColor} faceShape={faceShape} />
          </svg>
          <div className="flex gap-3">
            <Button variant="outline" onClick={randomize}>
              🎲 Randomize
            </Button>
            <Button onClick={downloadPng}>Download PNG</Button>
            <Button variant="outline" onClick={downloadSvg}>SVG</Button>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <label className="flex items-center gap-2">Size
              <select className="border border-border rounded-md bg-background px-2 py-1" value={exportSize} onChange={e => setExportSize(Number(e.target.value))}>
                <option value={256}>256 px</option><option value={512}>512 px</option><option value={1024}>1024 px</option>
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={circle} onChange={e => setCircle(e.target.checked)} /> Circle crop</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={transparent} onChange={e => setTransparent(e.target.checked)} /> Transparent background</label>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <Label className="mb-2 block text-sm">Background</Label>
            <ColorRow
              name="Background color"
              colors={BG_COLORS}
              value={bgColor}
              onChange={setBgColor}
            />
          </div>
          <div>
            <Label className="mb-2 block text-sm">Skin Tone</Label>
            <ColorRow
              name="Skin tone"
              colors={SKIN_COLORS}
              value={skinColor}
              onChange={setSkinColor}
            />
          </div>
          <div>
            <Label className="mb-2 block text-sm">Face Shape</Label>
            <StyleRow
              options={["round", "oval", "square"]}
              value={faceShape}
              onChange={v => setFaceShape(v as FaceShape)}
            />
          </div>
          <div>
            <Label className="mb-2 block text-sm">Hair Style</Label>
            <StyleRow
              options={["short", "bald", "bun", "long", "spiky"]}
              value={hairStyle}
              onChange={v => setHairStyle(v as HairStyle)}
            />
          </div>
          <div>
            <Label className="mb-2 block text-sm">Hair Color</Label>
            <ColorRow
              name="Hair color"
              colors={HAIR_COLORS}
              value={hairColor}
              onChange={setHairColor}
            />
          </div>
          <div>
            <Label className="mb-2 block text-sm">Eyes</Label>
            <StyleRow
              options={["normal", "happy", "wink", "surprised", "sad", "angry", "closed", "hearts"]}
              value={eyeStyle}
              onChange={v => setEyeStyle(v as EyeStyle)}
            />
          </div>
          <div>
            <Label className="mb-2 block text-sm">Mouth</Label>
            <StyleRow
              options={["smile", "grin", "neutral", "open", "sad", "smirk", "tongue", "worried"]}
              value={mouthStyle}
              onChange={v => setMouthStyle(v as MouthStyle)}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={glasses}
              onChange={e => setGlasses(e.target.checked)}
              id="glasses"
            />
            <Label htmlFor="glasses" className="text-sm">
              Glasses
            </Label>
          </div>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
